/**
 * The right-panel chart card. Owns the chart DOM - the empty
 * placeholder, the uPlot mount root, the fit readout, the tooltip node - plus the
 * header readouts (x-window, filter summary) and the legends (changeover strip,
 * scatter color legend). It hands its raw nodes to `runtime` on mount so the
 * imperative renderer never reaches into `document`; everything else renders from
 * the §10.8 bridge atoms.
 *
 * `#chart-root` is a bare div Lit never interpolates into - uPlot owns its
 * children, and Lit only toggles its visibility attribute, so the canvas
 * survives re-renders.
 */

import { html, nothing, type TemplateResult } from 'lit';
import { customElement, state } from 'lit/decorators.js';
import { LightElement } from '../../lit-base.ts';
import { StoreController } from '../../state/controller.ts';
import { attachDom, resetPlotData, runtime } from './plot-runtime.ts';
import { plotState } from '../../state/plot-state.ts';
import {
  chartEmpty,
  chartLayout,
  chartReady,
  plotFilterSummary,
  fitReadout,
  colorLegend,
  changeoverLegend,
  plotXWindow,
  lastPlotData,
  plottedSource,
} from './plot-ui-state.ts';
import { dataSources, sourceHasHistory } from '../../state/data-sources.ts';
import '../../ui/live-chip.ts';
import { liveTick, setLiveHover, toggleLivePause } from './live-tick.ts';
import { resizePlot, teardownPlotResizeObs, ensurePlotResizeObs } from './render/chart-dom.ts';
import { resetZoom } from './zoom.ts';
import { rerenderChartFromLast } from './plot-actions.ts';
import { exportChartImage, type ImageFormat } from './image-export.ts';
import './plot-settings-menu.ts';
import { allColumns, catalogAvailable, selectedShip } from '../../state/store.ts';
import { openSaveChartDialog } from '../catalog/save-chart-dialog.ts';
import { copyShareLink } from '../../state/presets.ts';
import { periodComparison, resolveComparisonPeriods } from '../../state/period-comparison.ts';
import { shipLabel } from '../../utils/ships.ts';
import { isTemporalType } from '../../utils/columns.ts';
import './slicer-strip.ts';

/**
 * Every mounted `<plot-chart>`, in mount order. The **last** one owns the
 * singleton chart DOM in `runtime`.
 *
 * There is only one `runtime.rootEl` / `gridEl` / `tooltipEl`, because the
 * renderer, tooltip, drag-zoom and image-export paths all read it directly. The
 * chart-builder modal needs a live chart too, so rather than making all of that
 * instance-keyed (the refactor in `docs/plans/dashboards.md`), ownership is a stack: the
 * modal mounts later and claims it, and when the modal closes the Explore chart
 * underneath reclaims it and redraws from cached data.
 */
const chartStack: PlotChart[] = [];

/** Hand the chart DOM to the newest mounted instance, or nobody. */
function refreshChartOwner(): void {
  const owner = chartStack[chartStack.length - 1];
  if (!owner) {
    attachDom(null);
    return;
  }
  owner.claimChartDom();
}

@customElement('plot-chart')
export class PlotChart extends LightElement {
  private store = new StoreController(this, [
    chartEmpty,
    chartLayout,
    chartReady,
    plotFilterSummary,
    fitReadout,
    colorLegend,
    changeoverLegend,
    plotXWindow,
    lastPlotData,
    plottedSource,
    dataSources,
    liveTick,
    plotState,
    catalogAvailable,
    periodComparison,
    selectedShip,
    allColumns,
  ]);

  /** Image-export dropdown open state (view-only). */
  @state() private exportMenuOpen = false;
  @state() private copied = false;

  private onWindowResize = (): void => resizePlot();

  // Close the export menu on any click outside the dropdown (blob behavior).
  private onDocClick = (e: MouseEvent): void => {
    if (!this.exportMenuOpen) return;
    const dropdown = this.querySelector('.chart-export-dropdown');
    if (dropdown && !dropdown.contains(e.target as Node)) this.exportMenuOpen = false;
  };

  /**
   * Point `runtime` at this instance's nodes and redraw whatever was last
   * plotted into them. Called on mount, and again when a chart above this one
   * in the stack goes away.
   */
  claimChartDom(): void {
    const root = this.querySelector<HTMLElement>('#chart-root');
    const grid = this.querySelector<HTMLElement>('#chart-grid');
    const tooltip = this.querySelector<HTMLElement>('#uplot-tooltip');
    const container = this.querySelector<HTMLElement>('#chart-container');
    if (!root || !grid || !tooltip || !container) return;
    teardownPlotResizeObs();
    attachDom({ root, grid, tooltip, container });
    ensurePlotResizeObs();
    // The previous owner's canvas went with its DOM, so the instance handle in
    // `runtime` points at nodes that are gone. Drop it and draw again here.
    const cached = {
      lastData: runtime.lastData,
      lastByShip: runtime.lastByShip,
      lastByPeriod: runtime.lastByPeriod,
      periodBaseResults: runtime.periodBaseResults,
      periodViewWindow: runtime.periodViewWindow,
      legsByShip: runtime.legsByShip,
      changeoversByShip: runtime.changeoversByShip,
      legsByPanel: runtime.legsByPanel,
      changeoversByPanel: runtime.changeoversByPanel,
    };
    resetPlotData();
    Object.assign(runtime, cached);
    rerenderChartFromLast();
    requestAnimationFrame(() => resizePlot());
  }

  override firstUpdated(): void {
    chartStack.push(this);
    refreshChartOwner();
    window.addEventListener('resize', this.onWindowResize);
    document.addEventListener('click', this.onDocClick);
  }

  override disconnectedCallback(): void {
    super.disconnectedCallback();
    window.removeEventListener('resize', this.onWindowResize);
    document.removeEventListener('click', this.onDocClick);
    const at = chartStack.indexOf(this);
    if (at >= 0) chartStack.splice(at, 1);
    teardownPlotResizeObs();
    // Hands the DOM back to the chart underneath - the Explore one, when a
    // builder modal closes - rather than leaving the app with none.
    refreshChartOwner();
  }

  private chooseExport(format: ImageFormat): void {
    this.exportMenuOpen = false;
    void exportChartImage(format);
  }

  private async copyChartLink(): Promise<void> {
    const ok = await copyShareLink({ pinContext: true });
    if (!ok) return;
    this.copied = true;
    setTimeout(() => (this.copied = false), 1400);
  }

  /* ── Image export dropdown (§10.10) ────────────────────── */

  private renderExportMenu(): TemplateResult | typeof nothing {
    if (!chartReady.value) return nothing;
    // Image export composes one uPlot canvas plus the DOM readouts around it.
    // Grid draws several canvases and none of the shared readouts, so there is
    // nothing single for it to compose - say so rather than exporting a blank.
    const gridActive = runtime.gridInstances.length > 0;
    const comparing = !!runtime.lastByPeriod;
    // The title sits on the wrapper, not the button: a disabled button receives
    // no pointer events, so its own tooltip never appears and it just reads as
    // broken. Same pattern as the Plot button.
    return html`<div
      class="chart-export-dropdown"
      title=${gridActive
        ? comparing
          ? 'Composite image export is not available for period comparisons yet'
          : 'Image export covers a single chart - switch to Overlay to export'
        : nothing}
    >
      <button
        class="chart-icon-btn"
        ?disabled=${gridActive}
        title=${gridActive ? nothing : 'Export chart image'}
        aria-haspopup="menu"
        aria-expanded=${this.exportMenuOpen}
        @click=${(e: Event) => {
          e.stopPropagation();
          if (gridActive) return;
          this.exportMenuOpen = !this.exportMenuOpen;
        }}
      >
        <span class="chart-action-glyph" aria-hidden="true">◫</span> Export
      </button>
      ${this.exportMenuOpen
        ? html`<div class="chart-export-menu" role="menu">
            <button class="chart-export-opt" role="menuitem" @click=${() => this.chooseExport('png')}>
              Export as PNG
            </button>
            <button class="chart-export-opt" role="menuitem" @click=${() => this.chooseExport('jpg')}>
              Export as JPG
            </button>
            <button class="chart-export-opt" role="menuitem" @click=${() => this.chooseExport('pdf')}>
              Export as PDF
            </button>
          </div>`
        : nothing}
    </div>`;
  }

  /* ── Reset zoom ────────────────────────────────────────── */

  /**
   * Keep this chart in the catalog, from the chart itself.
   *
   * You look at a chart, decide it is worth keeping, and the button is on it -
   * next to Share and Export, the other actions on the current chart.
   * Hidden entirely rather than disabled when there is no database, because
   * unlike Export it is not a thing this deployment can ever do.
   */
  private renderSaveToCatalog(): TemplateResult | typeof nothing {
    // Any drawn plot, not only a canvas one: tables and maps save too.
    if (chartEmpty.value || !catalogAvailable.value) return nothing;
    return html`<button
      class="chart-icon-btn"
      data-tour="save-chart"
      title="Save this chart to your catalog (S)"
      @click=${() => openSaveChartDialog()}
    >
      <span class="chart-action-glyph" aria-hidden="true">★</span> Save
    </button>`;
  }

  private renderShare(): TemplateResult | typeof nothing {
    if (chartEmpty.value) return nothing;
    return html`<button
      class="chart-icon-btn ${this.copied ? 'flash-ok' : ''}"
      title=${this.copied ? 'Link copied' : 'Copy a link to this chart'}
      aria-live="polite"
      @click=${() => void this.copyChartLink()}
    >
      ${this.copied
        ? html`<span class="chart-action-glyph" aria-hidden="true">✓</span> Copied`
        : html`<svg
              class="chart-action-glyph btn-glyph"
              viewBox="0 0 24 24"
              fill="none"
              aria-hidden="true"
            >
              <path
                d="M9.5 14.5l5-5M8 11l-2 2a3 3 0 104.2 4.2l2-2M16 13l2-2a3 3 0 10-4.2-4.2l-2 2"
                stroke="currentColor"
                stroke-width="1.8"
                stroke-linecap="round"
                stroke-linejoin="round"
              />
            </svg>
            Share`}
    </button>`;
  }

  private renderResetZoom(): TemplateResult | typeof nothing {
    const zoomed = !!plotXWindow.value?.isZoomed;
    if (!chartReady.value || !zoomed) return nothing;
    return html`<button
      class="chart-icon-btn"
      title="Back to the full x range (same as a double-click)"
      @click=${() => resetZoom()}
    >
      <span class="chart-action-glyph" aria-hidden="true">⤢</span> Reset zoom
    </button>`;
  }

  /* ── Live chip ─────────────────────────────────────────── */

  /** "LIVE · 12 s ago" while the chart on screen came from a live source. */
  private renderLiveChip(): TemplateResult | typeof nothing {
    const data = lastPlotData.value;
    if (!data || chartEmpty.value || sourceHasHistory(plottedSource.value)) return nothing;
    const newest = 'x_is_time' in data && data.x_is_time && 'last_x' in data ? data.last_x : null;
    const tick = liveTick.value;
    const mode = !tick.running ? 'static' : tick.paused ? 'paused' : 'ticking';
    return html`<live-chip
      .newest=${newest}
      .mode=${mode}
      .cadence=${tick.cadenceS}
      @live-toggle=${() => toggleLivePause()}
    ></live-chip>`;
  }

  /* ── Header x-window readout ───────────────────────────── */

  private renderXWindow(): TemplateResult | typeof nothing {
    const w = plotXWindow.value;
    if (!w) return nothing;
    return html`<span class="plot-x-window ${w.isZoomed ? 'is-zoomed' : ''}" title="Current x-axis window"
      >${w.a}<span class="pxw-sep">→</span>${w.b}</span
    >`;
  }

  /* ── Filter summary breadcrumb ─────────────────────────── */

  private renderFilterSummary(): TemplateResult | typeof nothing {
    const s = plotFilterSummary.value;
    if (!s) return nothing;
    // Promote AND/OR to joiners and comparators to tinted ops (blob markup).
    const parts: TemplateResult[] = [];
    const segs = s.split(/\s+(AND|OR)\s+/);
    segs.forEach((seg, i) => {
      if (i % 2 === 1) {
        parts.push(html`<span class="pfs-join">${seg}</span>`);
        return;
      }
      const tokens = seg.split(/(\s[=≠≥≤<>]\s)/);
      tokens.forEach((tok) => {
        const m = tok.match(/^\s([=≠≥≤<>])\s$/);
        if (m) parts.push(html` <span class="pfs-op">${m[1]}</span> `);
        else parts.push(html`${tok}`);
      });
    });
    return html`<div class="plot-filter-summary has-filters" title=${s}>
      <span class="pfs-label">Filters</span><span class="pfs-body">${parts}</span>
    </div>`;
  }

  /* ── Changeover legend strip ───────────────────────────── */

  private renderChangeoverLegend(): TemplateResult | typeof nothing {
    const cl = changeoverLegend.value;
    if (!cl) return nothing;
    if (cl.empty) {
      return html`<div class="changeover-legend-strip">
        <span class="co-legend-empty">No changeovers in this range.</span>
      </div>`;
    }
    return html`<div class="changeover-legend-strip">
      ${cl.groups.map(
        (g) => html`<span class="co-legend-group"
          ><span class="co-legend-swatch" style=${`background:${g.solid}`}></span><span>${g.display}</span></span
        >`,
      )}
      ${cl.engines.length
        ? html`<span class="co-legend-sep">·</span
            ><span class="co-legend-engines">Engines: ${cl.engines.join(', ')}</span>`
        : nothing}
    </div>`;
  }

  /* ── Fit readout (over the chart) ──────────────────────── */

  private renderFitReadout(): TemplateResult {
    const rows = fitReadout.value;
    return html`<div class="plot-fit-readout ${rows.length ? 'active' : ''}" id="plot-fit-readout">
      ${rows.map((r) =>
        r.error
          ? html`<div class="pfr-row error">
              <span class="pfr-swatch" style=${`--swatch:${r.color}`}></span>
              <span class="pfr-name">${r.series}</span>
              <span class="pfr-eq" title=${r.error}>fit failed: ${r.error}</span>
            </div>`
          : html`<div class="pfr-row">
              <span class="pfr-swatch" style=${`--swatch:${r.color}`}></span>
              <span class="pfr-name">${r.series}</span>
              <span class="pfr-eq" title=${r.eq ?? ''}>${r.eq ?? ''}</span>
              ${r.r2 != null ? html`<span class="pfr-r2">R²=${r.r2.toFixed(3)}</span>` : nothing}
            </div>`,
      )}
    </div>`;
  }

  /* ── Color legend (plot-meta) ──────────────────────────── */

  private renderColorLegend(): TemplateResult | typeof nothing {
    const cl = colorLegend.value;
    if (!cl) return nothing;
    // Discrete color-by legends render on the canvas (see render-scatter); only
    // the continuous gradient is shown as a DOM strip here.
    return html`<div class="plot-meta">
      <div class="color-legend">
        <span>${cl.min}</span>
        <div class="color-legend-bar" style=${`background:${cl.gradientCss}`}></div>
        <span>${cl.max}</span>
        <span style="margin-left:0.5rem"><strong>${cl.colorBy}</strong></span>
      </div>
    </div>`;
  }

  private renderComparisonIdentity(): TemplateResult | typeof nothing {
    const comparison = periodComparison.value;
    const ship = selectedShip.value;
    if (!comparison || !ship) return nothing;
    const periods = resolveComparisonPeriods(comparison);
    const xType = allColumns.value.find((column) => column.name === plotState.value.x_column)?.type;
    const temporal = plotState.value.x_column === 'timestampNDC' || (xType ? isTemporalType(xType) : false);
    const axis = temporal
      ? comparison.timeAxis === 'elapsed' ? 'Elapsed from start' : 'Actual dates (UTC)'
      : 'Numeric X';
    if (periods.length < 2) {
      return html`<span class="period-chart-identity">${shipLabel(ship)} · A only - add a period to compare</span>`;
    }
    return html`<span class="period-chart-identity">
      ${shipLabel(ship)} · ${periods.length} periods · ${axis}
      ${plotState.value.style.grid_shared_y ? nothing : html` · Independent Y scales`}
    </span>`;
  }

  override render(): TemplateResult {
    const empty = chartEmpty.value;
    const grid = chartLayout.value === 'grid';
    const isMap = lastPlotData.value?.plot_type === 'map';
    return html`
      <div class="card plot-card-wrap" data-tab="plot">
        <h2>
          <span class="chart-header-left">
            ${this.renderComparisonIdentity()}
            ${this.renderLiveChip()}${isMap ? nothing : this.renderResetZoom()}${this.renderXWindow()}
          </span>
          <div class="chart-header-actions">
            <plot-settings-menu></plot-settings-menu>
            ${this.renderSaveToCatalog()} ${this.renderShare()} ${this.renderExportMenu()}
          </div>
        </h2>
        ${this.renderFilterSummary()} ${this.renderChangeoverLegend()}
        <div
          id="chart-container"
          @mouseenter=${() => setLiveHover(true)}
          @mouseleave=${() => setLiveHover(false)}
        >
          <div class="plot-empty" id="plot-empty" style=${empty ? '' : 'display:none'}>
            <span class="empty-icon">◖</span>
            <span class="empty-text">${empty?.text ?? ''}</span>
            <span class="empty-sub">${empty?.sub ?? ''}</span>
          </div>
          <!-- Two mount points, never both visible: #chart-root holds the
               single uPlot (one ship or an overlay), #chart-grid holds one
               panel per ship. Lit never interpolates into either - it owns only
               their visibility, which is why the renderer drives that through
               the chartLayout atom instead of setting display on the nodes. -->
          <div id="chart-root" style=${empty || grid ? 'display:none' : ''}></div>
          <div id="chart-grid" class="chart-grid" style=${empty || !grid ? 'display:none' : ''}></div>
          ${this.renderFitReadout()}
          <div class="uplot-tooltip" id="uplot-tooltip"></div>
        </div>
        ${this.renderColorLegend()}
        <slicer-strip></slicer-strip>
      </div>
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'plot-chart': PlotChart;
  }
}
